import React, { useState } from 'react';
import {
  FiCheck,
  FiPlus,
  FiTrash2,
  FiChevronDown,
  FiChevronUp,
  FiCode,
  FiFileText,
  FiHash,
  FiSearch,
  FiCheckCircle,
  FiXCircle,
  FiInfo,
} from 'react-icons/fi';
import { IconType } from 'react-icons';

type AssertionFieldType = 'text' | 'number' | 'select' | 'array' | 'json' | 'checkbox';

interface AssertionFieldOption {
  value: string;
  label: string;
}

interface AssertionConfigField {
  name: string;
  type: AssertionFieldType;
  label: string;
  placeholder?: string;
  options?: AssertionFieldOption[];
}

interface AssertionTypeConfig {
  type: string;
  name: string;
  description: string;
  icon: IconType;
  configFields: AssertionConfigField[];
}

export const ASSERTION_TYPES: AssertionTypeConfig[] = [
  {
    type: 'contains',
    name: 'Contains',
    description: 'Check if output contains specific text',
    icon: FiSearch,
    configFields: [
      {
        name: 'values',
        type: 'array',
        label: 'Values to check',
        placeholder: 'Enter text values...',
      },
      {
        name: 'mode',
        type: 'select',
        label: 'Mode',
        options: [
          { value: 'all', label: 'Contains All' },
          { value: 'any', label: 'Contains Any' },
          { value: 'none', label: 'Contains None' },
        ],
      },
    ],
  },
  {
    type: 'regex',
    name: 'Regex Pattern',
    description: 'Match output against a regular expression',
    icon: FiCode,
    configFields: [
      { name: 'pattern', type: 'text', label: 'Pattern', placeholder: '^[A-Z].*\\.$' },
      { name: 'flags', type: 'text', label: 'Flags (optional)', placeholder: 'i, m, s' },
    ],
  },
  {
    type: 'json_schema',
    name: 'JSON Schema',
    description: 'Validate JSON output against a schema',
    icon: FiFileText,
    configFields: [
      {
        name: 'schema',
        type: 'json',
        label: 'JSON Schema',
        placeholder: '{"type": "object", "properties": {...}}',
      },
    ],
  },
  {
    type: 'length',
    name: 'Length',
    description: 'Check output length constraints',
    icon: FiHash,
    configFields: [
      { name: 'min', type: 'number', label: 'Minimum length', placeholder: '0' },
      { name: 'max', type: 'number', label: 'Maximum length', placeholder: '1000' },
      { name: 'exact', type: 'number', label: 'Exact length (optional)', placeholder: '' },
    ],
  },
  {
    type: 'factual_grounding',
    name: 'Factual Grounding',
    description: 'Verify claims against source documents (LLM-based)',
    icon: FiCheckCircle,
    configFields: [
      {
        name: 'source_key',
        type: 'text',
        label: 'Source context key',
        placeholder: 'context',
      },
      {
        name: 'strict',
        type: 'checkbox',
        label: 'Strict mode (fail on unsupported claims)',
      },
    ],
  },
];

const getAssertionType = (type: string): AssertionTypeConfig | undefined =>
  ASSERTION_TYPES.find((t) => t.type === type);

interface AssertionConfig {
  [key: string]: unknown;
}

interface AssertionItem {
  type: string;
  config?: AssertionConfig;
}

interface AssertionConfigEditorProps {
  assertion: AssertionItem;
  onChange: (assertion: AssertionItem) => void;
  onRemove?: () => void;
  disabled?: boolean;
  index: number;
}

const AssertionConfigEditor: React.FC<AssertionConfigEditorProps> = ({
  assertion,
  onChange,
  onRemove,
  disabled = false,
  index,
}) => {
  const [expanded, setExpanded] = useState(true);
  const typeConfig = getAssertionType(assertion.type);

  const updateConfig = (field: string, value: unknown) => {
    onChange({
      ...assertion,
      config: { ...(assertion.config ?? {}), [field]: value },
    });
  };

  const renderConfigField = (field: AssertionConfigField): React.ReactNode => {
    const value = assertion.config?.[field.name] ?? '';

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value as string}
            onChange={(e) => updateConfig(field.name, e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value as string}
            onChange={(e) =>
              updateConfig(field.name, e.target.value ? Number.parseInt(e.target.value) : '')
            }
            placeholder={field.placeholder}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
          />
        );

      case 'select': {
        return (
          <select
            value={(value as string) || field.options?.[0]?.value}
            onChange={(e) => updateConfig(field.name, e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
          >
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }

      case 'array': {
        const arrayValue = Array.isArray(value) ? (value as string[]) : [];
        return (
          <div className="space-y-2">
            {arrayValue.map((item, idx) => (
              <div key={`${field.name}-${idx}`} className="flex gap-2">
                <input
                  type="text"
                  value={item}
                  onChange={(e) => {
                    const newArray = [...arrayValue];
                    newArray[idx] = e.target.value;
                    updateConfig(field.name, newArray);
                  }}
                  placeholder={field.placeholder}
                  disabled={disabled}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newArray = arrayValue.filter((_, i) => i !== idx);
                    updateConfig(field.name, newArray);
                  }}
                  disabled={disabled}
                  className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => updateConfig(field.name, [...arrayValue, ''])}
              disabled={disabled}
              className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              <FiPlus className="w-4 h-4" /> Add value
            </button>
          </div>
        );
      }

      case 'json':
        return (
          <textarea
            value={typeof value === 'object' ? JSON.stringify(value, null, 2) : (value as string)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                updateConfig(field.name, parsed);
              } catch {
                updateConfig(field.name, e.target.value);
              }
            }}
            placeholder={field.placeholder}
            disabled={disabled}
            rows={4}
            className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
          />
        );

      case 'checkbox':
        return (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => updateConfig(field.name, e.target.checked)}
              disabled={disabled}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{field.label}</span>
          </label>
        );

      default:
        return null;
    }
  };

  const Icon = typeConfig?.icon ?? FiCheck;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className="px-4 py-3 bg-gray-50 dark:bg-gray-800 flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }} role="button" tabIndex={0}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="font-medium text-gray-900 dark:text-white">
            {typeConfig?.name ?? assertion.type}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">#{index + 1}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
            disabled={disabled}
            className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
          {expanded ? (
            <FiChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <FiChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Config Fields */}
      {expanded && (
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">{typeConfig?.description}</p>
          {typeConfig?.configFields?.map((field) => (
            <div key={field.name}>
              {field.type !== 'checkbox' && (
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {field.label}
                </span>
              )}
              {renderConfigField(field)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface AssertionSelectorProps {
  assertions?: AssertionItem[];
  onChange: (assertions: AssertionItem[]) => void;
  disabled?: boolean;
  maxAssertions?: number;
}

export const AssertionSelector: React.FC<AssertionSelectorProps> = ({
  assertions = [],
  onChange,
  disabled = false,
  maxAssertions = 10,
}) => {
  const [showAddMenu, setShowAddMenu] = useState(false);

  const addAssertion = (type: string) => {
    if (assertions.length >= maxAssertions) return;
    onChange([...assertions, { type, config: {} }]);
    setShowAddMenu(false);
  };

  const updateAssertion = (index: number, newAssertion: AssertionItem) => {
    const newAssertions = [...assertions];
    newAssertions[index] = newAssertion;
    onChange(newAssertions);
  };

  const removeAssertion = (index: number) => {
    onChange(assertions.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Assertions ({assertions.length})
        </span>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowAddMenu(!showAddMenu)}
            disabled={disabled || assertions.length >= maxAssertions}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiPlus className="w-4 h-4" />
            Add Assertion
          </button>

          {showAddMenu && (
            <div className="absolute right-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
              {ASSERTION_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.type}
                    type="button"
                    onClick={() => addAssertion(type.type)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-start gap-3 border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <Icon className="w-4 h-4 mt-0.5 text-gray-500 dark:text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white text-sm">
                        {type.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {type.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {assertions.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          <FiCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No assertions configured</p>
          <p className="text-xs mt-1">Add assertions to validate test case outputs</p>
        </div>
      ) : (
        <div className="space-y-3">
          {assertions.map((assertion, index) => (
            <AssertionConfigEditor
              key={`assertion-${assertion.type}-${index}`}
              assertion={assertion}
              onChange={(newAssertion) => updateAssertion(index, newAssertion)}
              onRemove={() => removeAssertion(index)}
              disabled={disabled}
              index={index}
            />
          ))}
        </div>
      )}

      {showAddMenu && (
        <div className="fixed inset-0 z-0" onClick={() => setShowAddMenu(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowAddMenu(false); }} role="presentation" />
      )}
    </div>
  );
};

interface AssertionResult {
  passed: boolean;
  assertion_type: string;
  message?: string;
  details?: string | object;
}

interface AssertionResultsDisplayProps {
  results?: AssertionResult[];
}

export const AssertionResultsDisplay: React.FC<AssertionResultsDisplayProps> = ({
  results = [],
}) => {
  if (results.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 dark:text-gray-400">
        <FiInfo className="w-6 h-6 mx-auto mb-1 opacity-50" />
        <p className="text-sm">No assertions were run</p>
      </div>
    );
  }

  const passedCount = results.filter((r) => r.passed).length;
  const failedCount = results.length - passedCount;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 text-sm">
        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
          <FiCheckCircle className="w-4 h-4" />
          {passedCount} passed
        </span>
        <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
          <FiXCircle className="w-4 h-4" />
          {failedCount} failed
        </span>
      </div>

      <div className="space-y-2">
        {results.map((result, index) => {
          const typeConfig = getAssertionType(result.assertion_type);
          const Icon = typeConfig?.icon ?? FiCheck;

          const borderClass = result.passed
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';

          const iconClass = result.passed
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400';

          const labelClass = result.passed
            ? 'text-green-800 dark:text-green-200'
            : 'text-red-800 dark:text-red-200';

          const messageClass = result.passed
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400';

          return (
            <div
              key={`result-${result.assertion_type}-${index}`}
              className={`p-3 rounded-lg border ${borderClass}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${iconClass}`} />
                  <span className={`font-medium ${labelClass}`}>
                    {typeConfig?.name ?? result.assertion_type}
                  </span>
                </div>
                {result.passed ? (
                  <FiCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                ) : (
                  <FiXCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                )}
              </div>
              {result.message && (
                <p className={`mt-1 text-sm ${messageClass}`}>{result.message}</p>
              )}
              {result.details && (
                <pre className="mt-2 p-2 bg-white dark:bg-gray-800 rounded text-xs overflow-x-auto">
                  {typeof result.details === 'string'
                    ? result.details
                    : JSON.stringify(result.details, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AssertionSelector;
